import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { TypedMemo } from '../../../common/types';
import { useApiAccessTokens } from './useApiAccessTokens';
import { ApiAccessRevealToken } from './ApiAccessRevealToken';
import { ApiAccessCreateForm } from './ApiAccessCreateForm';
import { ApiAccessTokenTable } from './ApiAccessTokenTable';
import { ApiUsageStrip } from './ApiUsageStrip';
import { ApiQuickstart } from './ApiQuickstart';
import { Panel, PanelBody, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ApiAccessCardProps {
  /** Daily browse credits from the caller's plan, shown in the usage strip. */
  dailyBrowseCredits?: number;
}

/**
 * The token lifecycle, end to end: what your plan allows, the one-time reveal,
 * the tokens you hold, and the first request to make with one.
 */
const ApiAccessCardComponent: React.FC<ApiAccessCardProps> = ({ dailyBrowseCredits }) => {
  const intl = useIntl();
  const {
    tokens,
    isLoading,
    error,
    refetch,
    justCreated,
    dismissCreated,
    createToken,
    isCreating,
    createError,
    resetCreateError,
    revokeToken,
    revokingId,
    isRevoking,
  } = useApiAccessTokens();

  // With no tokens there is nothing to do but mint one, so the form starts open.
  // Once you hold tokens the list is the point, and creating is a deliberate act.
  const [isFormOpen, setFormOpen] = useState(false);

  const openForm = useCallback(() => setFormOpen(true), []);
  const closeForm = useCallback(() => {
    setFormOpen(false);
    resetCreateError();
  }, [resetCreateError]);

  const onCreate = useCallback(
    (input: { name: string }) => {
      createToken(input);
      setFormOpen(false);
    },
    [createToken]
  );

  if (isLoading) {
    return <LoadingView />;
  }
  if (error) {
    return <ErrorView error={error} retry={refetch} />;
  }

  const showForm = isFormOpen || tokens.length === 0;

  return (
    <div>
      <div className={styles.section}>
        <ApiUsageStrip activeTokens={tokens.length} dailyBrowseCredits={dailyBrowseCredits} />
      </div>

      {justCreated && <ApiAccessRevealToken created={justCreated} onDismiss={dismissCreated} />}

      <Panel as="section" className={styles.section}>
        <PanelHead
          title={intl.formatMessage({ id: 'app.sml.settings.apiAccess.list.title', defaultMessage: 'Tokens' })}
          right={
            <>
              <span className={styles.hint}>
                {intl.formatMessage(
                  { id: 'app.sml.settings.apiAccess.list.activeCount', defaultMessage: '{count} active' },
                  { count: tokens.length }
                )}
              </span>
              {!showForm && (
                <Button color="primary" onClick={openForm}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  {intl.formatMessage({ id: 'app.sml.settings.apiAccess.create.new', defaultMessage: 'New token' })}
                </Button>
              )}
            </>
          }
        />

        {showForm && (
          <PanelBody>
            <ApiAccessCreateForm
              onCreate={onCreate}
              onCancel={tokens.length === 0 ? undefined : closeForm}
              isCreating={isCreating}
              createError={createError}
            />
          </PanelBody>
        )}

        <ApiAccessTokenTable tokens={tokens} onRevoke={revokeToken} revokingId={revokingId} isRevoking={isRevoking} />
      </Panel>

      <ApiQuickstart />
    </div>
  );
};

export const ApiAccessCard = TypedMemo(ApiAccessCardComponent);
